import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import { useAuth, formatApiError } from "../store/auth";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      const dest = location.state?.from?.pathname || "/dashboard";
      navigate(dest, { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "No se pudo iniciar sesión.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-lg p-8 shadow-sm">
        <div className="mb-6">
          <div className="text-[20px] font-semibold tracking-tight text-slate-900">Nimbus</div>
          <div className="text-[14px] text-slate-500 mt-1">
            Cumplimiento normativo para PYMEs
          </div>
        </div>

        <h1 className="text-[18px] font-semibold text-slate-900 mb-6">Iniciar sesión</h1>

        <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
          <div>
            <Label htmlFor="email">Correo electrónico</Label>
            <Input
              id="email"
              data-testid="login-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
            />
          </div>
          <div>
            <Label htmlFor="password">Contraseña</Label>
            <Input
              id="password"
              data-testid="login-password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
            />
          </div>

          {error && (
            <div data-testid="login-error" className="text-[12px] text-destructive">
              {error}
            </div>
          )}

          <Button
            type="submit"
            data-testid="login-submit"
            disabled={loading}
            className="w-full"
          >
            {loading ? "Iniciando sesión..." : "Iniciar sesión"}
          </Button>
        </form>

        <div className="my-6 flex items-center gap-3">
          <div className="flex-1 h-px bg-slate-200" />
          <span className="text-[12px] text-slate-400">o</span>
          <div className="flex-1 h-px bg-slate-200" />
        </div>

        <TooltipProvider>
          <div className="space-y-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="block">
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    disabled
                    data-testid="login-google"
                  >
                    Continuar con Google
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>Disponible próximamente</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="block">
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    disabled
                    data-testid="login-microsoft"
                  >
                    Continuar con Microsoft
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>Disponible próximamente</TooltipContent>
            </Tooltip>
          </div>
        </TooltipProvider>

        <div className="mt-6 text-center text-[14px] text-slate-600">
          ¿Aún no tienes cuenta?{" "}
          <Link to="/register" data-testid="login-to-register" className="text-primary font-medium hover:underline">
            Regístrate
          </Link>
        </div>
      </div>
    </div>
  );
}
